import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  HeroCard,
  ProgressSteps,
  Card,
  TipBox,
  PrimaryButton,
  SecondaryButton
} from '../shared';
import InviteFamilyModal from '../../familytree/InviteFamilyModal';
import { getUser } from '../../../utils/secureStorage';

/**
 * FreshStartState Component
 *
 * Dashboard state for brand new users with no connections and no stories
 * Goal: Get them to invite family members FIRST
 */
function FreshStartState({ user }) {
  const navigate = useNavigate();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Get familyId and personId from localStorage
  const getFamilyId = () => {
    const familyId = localStorage.getItem('familyId');
    if (familyId) return familyId;
    const userData = getUser() || {};
    return userData.family_id || userData.familyId || null;
  };

  const getPersonId = () => {
    const userData = getUser() || {};
    return userData.person_id || userData.personId || null;
  };

  const familyId = getFamilyId();
  const personId = getPersonId();

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = (memberWasAdded = false) => {
    setIsInviteModalOpen(false);
    if (memberWasAdded) {
      window.location.reload();
    }
  };

  const steps = [
    { label: 'Create Account', status: 'complete' },
    { label: 'Invite Family', status: 'current' },
    { label: 'Share Stories', status: 'pending' },
    { label: 'Enjoy!', status: 'pending' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5DC] p-6">
      <div className="max-w-4xl mx-auto">
        <HeroCard
          emoji="👋"
          title="Welcome to One Generation to Next!"
          subtitle="Your family is waiting to hear your stories. Let's get you connected!"
          variant="success"
        />

        <ProgressSteps steps={steps} />

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-3xl">👨‍👩‍👧‍👦</span>
            <h2 className="text-2xl font-bold text-gray-800">
              Start by Inviting Your Family
            </h2>
          </div>

          <p className="text-lg text-gray-700 mb-5 leading-relaxed">
            Your stories deserve an audience! Invite your children, grandchildren,
            and other family members so they can hear your wisdom and memories.
          </p>

          {/* Placeholder avatars */}
          <div className="flex items-center justify-center gap-4 my-6">
            {['Your Son', 'Your Daughter', 'Grandchild'].map((label, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full border-4 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400 text-3xl mb-2">
                  +
                </div>
                <span className="text-sm text-gray-600 font-semibold">{label}</span>
              </div>
            ))}
          </div>

          <PrimaryButton
            icon="✉️"
            onClick={handleOpenInviteModal}
          >
            Invite Family Members
          </PrimaryButton>

          <TipBox
            title="Why invite first?"
            text="When your family joins, they can ask you questions about your life — giving you the perfect prompts for stories they actually want to hear!"
          />
        </Card>

        <Card variant="dashed" className="text-center">
          <p className="text-lg text-gray-700 mb-4">
            Want to try it out first? You can record a private story before inviting anyone.
          </p>
          <SecondaryButton
            icon="🔒"
            onClick={() => navigate('/tell-a-story?private=true')}
            centered
          >
            Record Private Story
          </SecondaryButton>
        </Card>

        {/* Invite Family Modal */}
        <InviteFamilyModal
          isOpen={isInviteModalOpen}
          onClose={handleCloseInviteModal}
          familyId={familyId}
          rootPersonId={personId}
        />
      </div>
    </div>
  );
}

FreshStartState.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    avatarInitial: PropTypes.string.isRequired
  }).isRequired
};

export default FreshStartState;
